import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import EntityCardList from '../controls/EntityCardList';
import LocationMapperCountryState from '../controls/LocationMapperCountryState';
import DateField from '../fields/DateField';
import NavRow from '../fields/NavRow';
import TextField from '../fields/TextField';
import { TaskDefinition, TaskStepRenderArgs } from '../QuestionnaireShell';
import { LicenseRow, LicensesValues } from '../types';
import { zodResolver } from '../validation';

const licenseSchema = z.object({
  radio1: z.literal('License'),
  countryAndState: z.object({
    country: z.string().min(1, 'Country is required'),
    states: z
      .array(
        z.object({
          title: z.string(),
          value: z.string(),
          countryCode: z.string(),
        }),
      )
      .min(1, 'Select at least one state'),
  }),
  barNumber: z
    .string({ required_error: 'Bar Number is required but is missing' })
    .min(1, 'Bar Number is required but is missing'),
  admissionDate: z.string({ required_error: 'Admission Date is required' }).min(1, 'Admission Date is required'),
});

const schema = z.object({
  licenses: z.array(licenseSchema).min(1, 'Add at least one license'),
});

const makeEmptyRow = (): LicenseRow => ({
  radio1: 'License',
  countryAndState: { country: 'US', states: [] },
  barNumber: '',
  admissionDate: '',
});

const DEFAULTS: LicensesValues = { licenses: [] };

const formatDateDisplay = (iso: string) => {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const LicensesForm = ({ initialValues, onComplete, onBack, onCancelEdit, submitLabel }: TaskStepRenderArgs) => {
  const defaults = (initialValues as LicensesValues | undefined) ?? DEFAULTS;
  const {
    control,
    handleSubmit,
    trigger,
    formState: { isValid },
  } = useForm<LicensesValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const allLicenses = useWatch({ control, name: 'licenses' }) as LicenseRow[] | undefined;

  const onSubmit = handleSubmit((values) => onComplete(values));

  return (
    <form className="rnq-form" onSubmit={(e) => e.preventDefault()} style={{ marginTop: '1rem' }}>
      <EntityCardList<LicensesValues, LicenseRow>
        control={control}
        name="licenses"
        entityLabel="License"
        trigger={trigger}
        makeEmptyRow={makeEmptyRow}
        isRowValid={(row) => licenseSchema.safeParse(row).success}
        renderers={{
          getPrimary: (row) => row.countryAndState?.states?.map((s) => s.title).join(' ') ?? '',
          getSecondary: (row) => row.barNumber ?? null,
          getTertiary: (row) => (row.admissionDate ? formatDateDisplay(row.admissionDate) : null),
          secondaryIcon: 'id_gavel',
          tertiaryIcon: 'calendar_days',
        }}
        renderEditForm={(index) => {
          const excludedStateCodes = (allLicenses ?? []).flatMap((row, i) =>
            i === index ? [] : (row?.countryAndState?.states ?? []).map((s) => s.value),
          );
          return (
            <>
              <LocationMapperCountryState
                control={control}
                name={`licenses.${index}.countryAndState`}
                required
                excludedStateCodes={excludedStateCodes}
              />
              <TextField control={control} name={`licenses.${index}.barNumber`} label="Bar Number" required />
              <DateField
                control={control}
                name={`licenses.${index}.admissionDate`}
                label="Admission Date"
                required
                disableFuture
              />
            </>
          );
        }}
      />
      <NavRow
        onBack={onBack}
        onCancelEdit={onCancelEdit}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={!isValid}
      />
    </form>
  );
};

const Licenses: TaskDefinition = {
  key: 'task5',
  label: 'Licenses',
  title: 'Where are you licensed to practice?',
  description: 'Please provide details',
  render: (args) => <LicensesForm {...args} />,
};

export default Licenses;
